import { FC, useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { useClientPricing } from "@hooks/billing/useClientPricing";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Tag } from "@design-system/Tag/Tag";
import { LeftArrowIcon } from "@icons";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { ClientStep } from "./AddClientSteps/ClientStep";
import { isOrganizationOwnedPricingBlocked } from "./AddClientSteps/planHelpers";
import { PlanStep } from "./AddClientDrawerSteps/PlanStep";
import {
    ClientFormSchema,
    AddClientMode,
    addEntityClientStepSchema,
    clientStepSchema,
    inviteStepSchema,
    planStepSchema,
} from "./AddClientSteps/lib";
import {
    AddClientDrawerStep,
    DRAWER_STEP_CONFIG,
} from "./AddClientDrawerSteps/lib";
import { SetupMethodStep } from "./AddClientDrawerSteps/SetupMethodStep";
import { SelectLedgerStep } from "./AddClientDrawerSteps/SelectLedgerStep";
import { HistoricalDataStep } from "./AddClientDrawerSteps/HistoricalDataStep";
import { MultiEntityStep } from "./AddClientDrawerSteps/MultiEntityStep";
import { AddAnotherEntityStep } from "./AddClientDrawerSteps/AddAnotherEntityStep";
import { ResultStep } from "./AddClientDrawerSteps/ResultStep";
import { AddClientDrawerStepper } from "./AddClientDrawerStepper";
import styles from "./AddClientDrawerStepsContainer.module.scss";
import drawerStyles from "./AddClientDrawerSteps/AddClientDrawerSteps.module.scss";
import { InviteStep } from "./AddClientDrawerSteps/InviteStep";

interface Props {
    currentStep: AddClientDrawerStep;
    onStepChange: (step: AddClientDrawerStep) => void;
    onClose: () => void;
    mode: AddClientMode;
    initialMode: AddClientMode;
    clientId?: string;
    isEntityLimitReached: boolean;
    onAddAnotherEntity: () => void;
    onAddAnotherClient: () => void;
    loadingAction: "invite" | "skip" | null;
    qboConnection?: IntegrationConnection | null;
    setQboConnection: (connection: IntegrationConnection | null) => void;
    entityCount: number;
    clientName: string;
    planLabel: string;
    billingTransferEnabled: boolean;
    invitedCount: number;
    submitWithoutInvite: () => Promise<void>;
}

export const AddClientDrawerStepsContainer: FC<Props> = ({
    currentStep,
    onStepChange,
    onClose,
    mode,
    initialMode,
    clientId,
    isEntityLimitReached,
    onAddAnotherEntity,
    onAddAnotherClient,
    loadingAction,
    qboConnection,
    setQboConnection,
    entityCount,
    clientName,
    planLabel,
    billingTransferEnabled,
    invitedCount,
    submitWithoutInvite,
}) => {
    const { values, submitForm, isSubmitting, setSubmitting } =
        useFormikContext<ClientFormSchema>();
    const clientPricing = useClientPricing();
    const stepOrder = useMemo(
        () =>
            DRAWER_STEP_CONFIG.map((c) => c.id).filter((step) => {
                if (mode === "newClient") {
                    if (
                        step === AddClientDrawerStep.Invite &&
                        !values.plan.billingOwnership
                    ) {
                        return false;
                    }
                    return step !== AddClientDrawerStep.AddAnotherEntity;
                }
                return (
                    step !== AddClientDrawerStep.SelectPlan &&
                    step !== AddClientDrawerStep.MultiEntity &&
                    step !== AddClientDrawerStep.Invite
                );
            }),
        [mode, values.plan.billingOwnership],
    );

    const isCurrentStepValid = useMemo(() => {
        switch (currentStep) {
            case AddClientDrawerStep.SetupMethod:
            case AddClientDrawerStep.SelectLedger:
            case AddClientDrawerStep.HistoricalData:
                return true;

            case AddClientDrawerStep.ClientDetails:
                return (
                    mode === "addEntity"
                        ? addEntityClientStepSchema
                        : clientStepSchema
                ).safeParse(values.client).success;

            case AddClientDrawerStep.MultiEntity:
                return values.isMultiEntity !== null;

            case AddClientDrawerStep.SelectPlan:
                return (
                    planStepSchema.safeParse(values.plan).success &&
                    !isOrganizationOwnedPricingBlocked({
                        clientPricing,
                        isOrgOwned: values.plan.billingOwnership,
                    })
                );

            case AddClientDrawerStep.Invite:
                return inviteStepSchema.safeParse(values.invite).success;

            case AddClientDrawerStep.Result:
                return true;

            default:
                return false;
        }
    }, [currentStep, mode, values, clientPricing]);

    const showEntityNumberTag =
        initialMode === "newClient" &&
        mode === "addEntity" &&
        entityCount >= 1 &&
        currentStep !== AddClientDrawerStep.AddAnotherEntity &&
        currentStep !== AddClientDrawerStep.Result;

    const isFirstStep = currentStep === stepOrder[0];
    const lastFormStep = useMemo(() => {
        if (mode === "addEntity") {
            return AddClientDrawerStep.ClientDetails;
        }
        return values.plan.billingOwnership
            ? AddClientDrawerStep.Invite
            : AddClientDrawerStep.SelectPlan;
    }, [mode, values.plan.billingOwnership]);
    const isLastFormStep = currentStep === lastFormStep;
    const isResultStep = currentStep === AddClientDrawerStep.Result;
    const stepOwnsFooter =
        currentStep === AddClientDrawerStep.SetupMethod ||
        currentStep === AddClientDrawerStep.HistoricalData ||
        currentStep === AddClientDrawerStep.AddAnotherEntity;

    const goNext = useCallback(async () => {
        if (isLastFormStep) {
            try {
                await submitForm();
            } catch {
                setSubmitting(false);
            }
            return;
        }

        const idx = stepOrder.indexOf(currentStep);
        const nextStep = stepOrder[idx + 1];

        if (nextStep) {
            onStepChange(nextStep);
        }
    }, [
        currentStep,
        isLastFormStep,
        onStepChange,
        submitForm,
        setSubmitting,
        stepOrder,
    ]);

    const goPrev = useCallback(() => {
        const idx = stepOrder.indexOf(currentStep);
        const prevStep = stepOrder[idx - 1];

        if (prevStep) {
            onStepChange(prevStep);
        }
    }, [currentStep, onStepChange, stepOrder]);

    const renderStepContent = () => {
        switch (currentStep) {
            case AddClientDrawerStep.SetupMethod:
                return (
                    <SetupMethodStep
                        onNext={goNext}
                        qboConnection={qboConnection}
                        setQboConnection={setQboConnection}
                        workspaceId={
                            mode === "addEntity" ? clientId : undefined
                        }
                    />
                );

            case AddClientDrawerStep.SelectLedger:
                return <SelectLedgerStep />;

            case AddClientDrawerStep.HistoricalData:
                return (
                    <HistoricalDataStep
                        qboConnection={qboConnection}
                        setQboConnection={setQboConnection}
                        onNext={goNext}
                        onPrev={goPrev}
                        workspaceId={
                            mode === "addEntity" ? clientId : undefined
                        }
                    />
                );

            case AddClientDrawerStep.ClientDetails:
                return (
                    <div className={drawerStyles.container}>
                        <div className={drawerStyles.header}>
                            Client details
                        </div>
                        <ClientStep mode={mode} />
                    </div>
                );

            case AddClientDrawerStep.MultiEntity:
                return <MultiEntityStep />;

            case AddClientDrawerStep.SelectPlan:
                return <PlanStep clientPricing={clientPricing} />;

            case AddClientDrawerStep.Invite:
                return <InviteStep />;

            case AddClientDrawerStep.AddAnotherEntity:
                return (
                    <AddAnotherEntityStep
                        onContinue={() =>
                            onStepChange(AddClientDrawerStep.Result)
                        }
                        onAddAnotherEntity={onAddAnotherEntity}
                        canAddAnotherEntity={
                            !!clientId && !isEntityLimitReached
                        }
                    />
                );

            case AddClientDrawerStep.Result:
                return (
                    <ResultStep
                        onClose={onClose}
                        clientName={clientName}
                        entityCount={entityCount}
                        planLabel={planLabel}
                        onAddAnotherClient={onAddAnotherClient}
                        isAddEntityOnlyFlow={initialMode === "addEntity"}
                        billingTransferEnabled={
                            initialMode !== "addEntity" &&
                            billingTransferEnabled
                        }
                        invitedCount={
                            initialMode === "addEntity" ? 0 : invitedCount
                        }
                    />
                );

            default:
                return null;
        }
    };

    const nextStepLabel = useMemo(() => {
        if (currentStep === AddClientDrawerStep.Invite) {
            return "Invite";
        }
        if (isLastFormStep) {
            return mode === "addEntity" ? "Add entity" : "Create client";
        }
        return "Next";
    }, [currentStep, isLastFormStep, mode]);

    return (
        <div className={styles.container}>
            <AddClientDrawerStepper currentStep={currentStep} mode={mode} />
            <div className={styles.stepContent}>
                {showEntityNumberTag && (
                    <div className={styles.entityNumberTagWrapper}>
                        <Tag color="accent">ENTITY {entityCount + 1}</Tag>
                    </div>
                )}
                <div>{renderStepContent()}</div>

                {!isResultStep && !stepOwnsFooter && (
                    <div className={styles.footer}>
                        {!isFirstStep ? (
                            <Button
                                variant="primary"
                                type="button"
                                onClick={goPrev}
                                iconOnly
                                size="xl"
                                aria-label="Go back"
                            >
                                <LeftArrowIcon />
                            </Button>
                        ) : (
                            <div />
                        )}

                        {currentStep === AddClientDrawerStep.Invite && (
                            <ButtonWithLoader
                                variant="secondary"
                                type="button"
                                size="xl"
                                loading={loadingAction === "skip"}
                                disabled={
                                    loadingAction === "invite" || isSubmitting
                                }
                                className={styles.skipInviteButton}
                                onClick={submitWithoutInvite}
                            >
                                Skip
                            </ButtonWithLoader>
                        )}

                        <ButtonWithLoader
                            variant="primary"
                            type="button"
                            size="xl"
                            disabled={
                                !isCurrentStepValid || loadingAction === "skip"
                            }
                            loading={
                                isLastFormStep &&
                                (isSubmitting || loadingAction === "invite")
                            }
                            onClick={goNext}
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: "Next",
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                        >
                            {nextStepLabel}
                        </ButtonWithLoader>
                    </div>
                )}
            </div>
            <div />
        </div>
    );
};
